import { EnrichedVideoEditorEditListItemDto } from '@app/modules/video-editions/types';
import { updateQueryParam, useParamDrivenValue, useRouter } from '@mi-front/core';
import { useEffect, useMemo } from 'react';
import { isVideoEditorEditRenderCompleted } from '../lib/is-video-editor-edit-render-completed';
import { VIDEO_EDITIONS_EDIT_GROUP_QUERY_PARAM } from '../lib/video-editions-edit-group-query-param';

export type VideoEditionsEditCategoryTab = 'draft' | 'generated';

const VALID_TABS: VideoEditionsEditCategoryTab[] = ['draft', 'generated'];

/**
 * Splits video editor edits into drafts (not fully rendered) vs completed renders, syncs the active group
 * with the URL (`editGroup`), and corrects the URL when the current group has no items but the other does.
 */
export function useVideoEditionsEditCategoryTabs(
  edits: EnrichedVideoEditorEditListItemDto[] | undefined,
) {
  const { history } = useRouter();
  const { value: categoryRaw } = useParamDrivenValue({
    param: VIDEO_EDITIONS_EDIT_GROUP_QUERY_PARAM,
    validValues: VALID_TABS,
    defaultValue: 'draft',
  });

  const category = categoryRaw as VideoEditionsEditCategoryTab;

  const { draftEdits, generatedEdits } = useMemo(() => {
    const list = edits ?? [];
    const draft: EnrichedVideoEditorEditListItemDto[] = [];
    const generated: EnrichedVideoEditorEditListItemDto[] = [];
    for (const item of list) {
      if (isVideoEditorEditRenderCompleted(item)) {
        generated.push(item);
      } else {
        draft.push(item);
      }
    }
    return { draftEdits: draft, generatedEdits: generated };
  }, [edits]);

  /**
   * Correct invalid tab vs data (e.g. editGroup=draft but no drafts). Uses replace so browser back
   * can clear the query without this effect immediately pushing the same param again.
   */
  useEffect(() => {
    if (!edits?.length) return;
    if (category === 'draft' && draftEdits.length === 0 && generatedEdits.length > 0) {
      updateQueryParam(history, VIDEO_EDITIONS_EDIT_GROUP_QUERY_PARAM, 'generated', false);
    } else if (category === 'generated' && generatedEdits.length === 0 && draftEdits.length > 0) {
      updateQueryParam(history, VIDEO_EDITIONS_EDIT_GROUP_QUERY_PARAM, 'draft', false);
    }
  }, [edits, draftEdits.length, generatedEdits.length, history, category]);

  return { draftEdits, generatedEdits };
}
